import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {DbValidators} from "../../../../validators/DbValidators";
import {Config} from "../../../../../config/config";
import {CreateWebsiteHelpersService} from "../../../../../services/local/create-website-helpers.service";
import {
  IWebsiteInstallationForm,
  IWebsiteInstallData,
  WebsiteInstallData
} from "../../../../../components/common/create-website/models/website-install-data.model";
import {OptionRegion} from "../../../../models/option-region.model";
import {filter, first, switchMap} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {User} from "../../../../../user/user";
import {Utils} from "../../../../utils/utils";
import {WebsiteService} from "../../../../../websites/website.service";

@Component({
    selector: 'db-ecommerce-complete-setup',
    templateUrl: './ecommerce-complete-setup.component.html',
    styleUrls: ['./ecommerce-complete-setup.component.scss'],
    standalone: false
})
export class EcommerceCompleteSetupComponent implements OnInit, OnChanges {

  @Input() subscription: UserSubscription;
  @Input() suggestedSubdomain = '';
  @Input() upgradeableSubscription: UserSubscription | null;
  @Input() userLocalStorage: any;
  @Input() regions: OptionRegion[] = [];
  @Output() onFinalize: EventEmitter<IWebsiteInstallData> = new EventEmitter();

  public formSubmitted = false;
  public configHostname: string = Config.hostname;
  public createWebsiteForm: FormGroup = new FormGroup({
    websiteTitle: new FormControl(null, [DbValidators.trimStart, Validators.required]),
    region: new FormControl(null, Validators.required),
    subdomain: new FormControl(null, [DbValidators.trim, Validators.required, Validators.pattern('^([a-z0-9]+(-[a-z0-9]+)*)$')]),
  });
  private _user: User;
  private _websiteData: IWebsiteInstallData;
  public UserSubscriptionCategory = UserSubscriptionCategory;
   isMobile: boolean = false;

  constructor(
    private readonly _store: Store<fromStore.DbStoreState>,
    private _createWebsiteHelpersService: CreateWebsiteHelpersService,
    private _websiteService: WebsiteService,
  ) { }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 768;
    this._runInitialSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['regions'] && changes['regions'].currentValue && changes['regions'].currentValue.length) {
      this._setAvailableRegion();
    }
  }

  private _runInitialSubscriptions(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUser)),
        first()
      )
      .subscribe((user) => {
        this._user = user;
        this._getInitialData(user.subscriptionId);
      });
  }

  private _getInitialData(subscriptionId): void {
    this._setWebsiteTitle();
    this._createWebsiteHelpersService.getWebsiteCreationInitialData(subscriptionId)
      .then(([suggestedSubDomain, regions]) => {
        this.suggestedSubdomain = suggestedSubDomain;
        this.suggestedSubdomain && this.createWebsiteForm.get('subdomain').setValue(this.suggestedSubdomain);
      });
    this._setAvailableRegion();
  }

  private _setWebsiteTitle(): void {
    this._websiteService.getAiBuilderDemoWebsite()
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          const websiteTitle =  response.data.site_title ? response.data.site_title : 'My Website';
          this.createWebsiteForm.get('websiteTitle').setValue(websiteTitle);
        }
      });
  }

  private _setAvailableRegion(): void {
    const availableRegion = this.regions.find(region => region.isAvailable);
    availableRegion && this.createWebsiteForm.get('region').setValue(availableRegion.value);
  }

  public submit(): void {
    this.formSubmitted = true;
    if (!this.createWebsiteForm.get('websiteTitle').value) {
      this.createWebsiteForm.get('websiteTitle').setErrors({required: true});
      return;
    }
    this._checkSubdomain();
  }

  private _checkSubdomain(): void {
    this._createWebsiteHelpersService.checkSubDomain(this.createWebsiteForm.value.subdomain)
      .then(response => {
        if (!response.success) {
          this.createWebsiteForm.get('subdomain').setErrors({taken: response.message});
          return;
        }
        this._proceed();
      });
  }

  private _proceed(): void {
    const websiteData = this.createWebsiteForm.value;
    websiteData.userName = websiteData.subdomain;
    websiteData.password = Utils.generateRandomPassword(12);
    this._websiteData = this._websiteDataMap(websiteData);
    this.onFinalize.emit(this._websiteData);
  }

  /** map new Data into old website creation data */
  private _websiteDataMap(websiteData: IWebsiteInstallationForm): IWebsiteInstallData {
    return new WebsiteInstallData(websiteData);
  }

}
